import { faAdd, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DeleteSchoolModal from "components/modals/DeleteSchoolModal";
import PageHeading from "components/shared/PageHeading";
import CustomPagination from "components/shared/Pagination";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  Button,
  Table,
  Placeholder,
  Image,
  InputGroup,
  Form,
  Dropdown,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  deleteStudySet,
  getAllVocabulary,
} from "services/Teacher/Vocabulary.service";
import nodataimage from "../../../assets/nodataimage.svg";
import Flag from "react-world-flags";
import { getAllSubject } from "services/Teacher/Exercise.service";
import { listFormDrafts } from "services/formDraft.service";
import { useTranslation } from "react-i18next";

// Maps each vocabulary draft key to how it should appear/resume in the list.
const VOCAB_DRAFT_META = {
  "vocab-draft-ai": {
    label: "AI study set",
    resume: "/vocabulary/create-vocabulary/AI",
    sourceKey: "sourceLanguage",
    targetKey: "translationLanguage",
    sizeKey: "studySetSize",
    titleKey: "userPrompt",
  },
  "vocab-draft-manual": {
    label: "Manual study set",
    resume: "/vocabulary/create-vocabulary/manually",
    sourceKey: "selectedSourceLanguage",
    targetKey: "selectedTargetLanguage",
    sizeKey: "rangeValue",
    titleKey: "title",
  },
};

const Vocabulary = ({ embedded = false, headerPortal = null }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [vocabulary, setVocabulary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedSubject, setSelectedSubject] = useState(null);
  const [search, setSearch] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [selectedTranslation, setSelectedTranslation] = useState(null);
  const [selectedSort, setSelectedSort] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const sortOptions = ["A to Z", "Z to A", "Latest", "Oldest"];

  // Resolve a subject id (stored in a draft) back to its title for display.
  const subjectLabel = (id) =>
    subjects?.find((s) => s.value === id)?.label || "";

  // Load the user's in-progress vocabulary drafts (AI + Manual). Only drafts
  // that have actual content (a title/prompt) are surfaced as list rows.
  const fetchDrafts = async () => {
    try {
      const res = await listFormDrafts("vocab-draft");
      const rows = (res?.data?.result || [])
        .map((d) => {
          const meta = VOCAB_DRAFT_META[d.key];
          if (!meta) return null;
          const data = d.data || {};
          const title = (data[meta.titleKey] || "").trim();
          if (!title) return null; // untouched draft — don't clutter the list
          return {
            key: d.key,
            resume: meta.resume,
            title,
            typeLabel: meta.label,
            sourceId: data[meta.sourceKey],
            targetId: data[meta.targetKey],
            size: data[meta.sizeKey],
            updatedAt: d.updatedAt,
          };
        })
        .filter(Boolean);
      setDrafts(rows);
    } catch (error) {
      /* drafts are best-effort — never block the list */
    }
  };

  const fetchVocabulary = async (
    page = 1,
    subject = "",
    translation = "",
    search = "",
    sort = ""
  ) => {
    setLoading(true);
    try {
      const response = await getAllVocabulary(
        page,
        subject,
        translation,
        search,
        sort
      );
      setVocabulary(response.data.results.studySets || []);
      setTotalPages(response.data.results.totalPages || 1);
      setCurrentPage(page);
      setSelectedSubject(subject);
      setSelectedTranslation(translation);
      setSelectedSort(sort);
    } catch (error) {
      console.log("Error fetching libraries:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubject = async () => {
    try {
      const response = await getAllSubject();
      const formattedSubjects = response?.data?.results?.subject?.map(
        (subject) => ({
          value: subject._id,
          label: subject.title,
        })
      );
      setSubjects(formattedSubjects);
    } catch (error) {
      console.error("Error fetching school details:", error);
    }
  };

  useEffect(() => {
    fetchSubject();
    fetchVocabulary(1);
    fetchDrafts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (page) => {
    fetchVocabulary(page, selectedSubject, selectedTranslation, search, selectedSort);
  };

  const getFlagCode = (languageTitle) => {
    switch (languageTitle) {
      case "English":
        return "GB"; // UK flag for English
      case "Swedish":
        return "SE"; // Swedish flag
      case "Italian":
        return "IT"; // Italian flag
      case "French":
        return "FR"; // French flag
      case "Spanish":
        return "ES"; // Spanish flag
      default:
        return "GB"; // Default to UK flag if the language is not recognized
    }
  };

  const handleShowModal = (id) => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  const handleCloseModal = () => {
    setShowDeleteModal(false);
    setSelectedId(null);
  };

  const handleStudySetDelete = async () => {
    if (!selectedId) return;

    const toastId = toast.loading(t("Deleting..."));
    try {
      await deleteStudySet(selectedId);
      setShowDeleteModal(false);
      toast.update(toastId, {
        render: t("Deleted successfully!"),
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      fetchVocabulary(currentPage);
    } catch (error) {
      console.error("Error deleting the study set:", error);
      toast.update(toastId, {
        render: t("Failed to delete the study set."),
        type: "error",
        isLoading: false,
        autoClose: 3000,
      });
    }
  };

  const filterToggleStyle = {
    border: "1px solid #e3e3e6",
    backgroundColor: "#fff",
    color: "#323232",
    borderRadius: "30px",
    padding: "8px 18px",
    fontWeight: 500,
    fontSize: "14px",
  };

  // Filter controls, rendered either inline (standalone page) or portaled into
  // the shared header row when embedded under the My Exercises tabs.
  const filterControls = (
    <>
          {/* Source language filter */}
          <Dropdown>
            <Dropdown.Toggle variant="light" id="dropdown-language" style={filterToggleStyle}>
              {(selectedSubject && selectedSubject !== "All" && t(selectedSubject)) ||
                t("Select Language")}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() =>
                  fetchVocabulary(1, "All", selectedTranslation, search, selectedSort)
                }
              >
                {t("All")}
              </Dropdown.Item>
              {subjects?.map((item) => (
                <Dropdown.Item
                  key={item?.label}
                  onClick={() =>
                    fetchVocabulary(1, item?.label, selectedTranslation, search, selectedSort)
                  }
                >
                  {t(item?.label)}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          {/* Target (translation) language filter */}
          <Dropdown>
            <Dropdown.Toggle variant="light" id="dropdown-translation" style={filterToggleStyle}>
              {(selectedTranslation && selectedTranslation !== "All" && t(selectedTranslation)) ||
                t("Select Translation")}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() =>
                  fetchVocabulary(1, selectedSubject, "All", search, selectedSort)
                }
              >
                {t("All")}
              </Dropdown.Item>
              {subjects?.map((item, index) => (
                <Dropdown.Item
                  key={index}
                  onClick={() =>
                    fetchVocabulary(1, selectedSubject, item?.label, search, selectedSort)
                  }
                >
                  {t(item?.label)}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          {/* Sort */}
          <Dropdown>
            <Dropdown.Toggle variant="light" id="dropdown-sort" style={filterToggleStyle}>
              {t(selectedSort) || t("Select Sort")}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {sortOptions.map((item) => (
                <Dropdown.Item
                  key={item}
                  onClick={() =>
                    fetchVocabulary(1, selectedSubject, selectedTranslation, search, item)
                  }
                >
                  {t(item)}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          {/* Search */}
          <Form.Group className="position-relative mb-0" style={{ width: "220px" }}>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder={t("Search Here...")}
                className="px-3 py-2 border-end-0"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    fetchVocabulary(1, selectedSubject, selectedTranslation, search, selectedSort);
                  }
                }}
                style={{
                  borderTopLeftRadius: "30px",
                  borderBottomLeftRadius: "30px",
                  borderColor: "#e3e3e6",
                  borderRight: 0,
                  fontSize: "14px",
                }}
              />
              <InputGroup.Text
                className="bg-white"
                onClick={() =>
                  fetchVocabulary(1, selectedSubject, selectedTranslation, search, selectedSort)
                }
                style={{
                  cursor: "pointer",
                  borderTopRightRadius: "30px",
                  borderBottomRightRadius: "30px",
                  borderColor: "#e3e3e6",
                }}
              >
                <FontAwesomeIcon icon={faSearch} style={{ color: "#8c8c8c" }} />
              </InputGroup.Text>
            </InputGroup>
          </Form.Group>

          <Button
            className="py-2 border-0 button-style"
            onClick={() => navigate("/vocabulary/create-vocabulary")}
          >
            <FontAwesomeIcon className="pe-2" icon={faAdd} />
            {t("Create Study set")}
          </Button>
    </>
  );

  return (
    <div className="exercise-page">
      {/* Header: title + subtitle + inline filters + primary action. When
          embedded, the filters are portaled up into the shared header row. */}
      {headerPortal ? (
        createPortal(filterControls, headerPortal)
      ) : (
        <div className="exercise-header">
          <div>
            {!embedded && <PageHeading heading={"Vocabulary"} />}
            {!embedded && (
              <p className="exercise-header-subtitle">
                {t("Browse and manage your study sets.")}
              </p>
            )}
          </div>
          <div className="exercise-header-controls">{filterControls}</div>
        </div>
      )}

      {/* Results table */}
      <div className="exercise-page-body">
        <div className="exercise-results">
          <div
            className="bg-white exercise-table-scroll"
            style={{
              borderRadius: "10px",
              boxShadow: "rgba(100, 100, 111, 0.15) 0px 4px 20px 0px",
            }}
          >
            <Table hover className="mb-0 align-middle">
              <thead>
                <tr style={{ fontSize: "13px", color: "#8c8c8c" }}>
                  <th className="py-3 fw-semibold ps-4">{t("Title")}</th>
                  <th className="py-3 fw-semibold">{t("Subject")}</th>
                  <th className="py-3 fw-semibold">{t("Translation")}</th>
                  <th className="py-3 fw-semibold">{t("Description")}</th>
                  <th className="py-3 fw-semibold">{t("Size")}</th>
                  <th className="py-3 fw-semibold">{t("Status")}</th>
                  <th className="py-3 fw-semibold">{t("Source")}</th>
                  <th className="py-3 fw-semibold pe-4">{t("Assigned by")}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({ length: 8 }).map((__, ci) => (
                        <td key={ci} className="py-3">
                          <Placeholder animation="glow">
                            <Placeholder xs={5} />
                          </Placeholder>
                        </td>
                      ))}
                    </tr>
                  ))
                ) : drafts.length === 0 && vocabulary?.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-5">
                      <Image src={nodataimage} alt="No Data" className="img-fluid" />
                      <p className="mt-2 text-secondary">
                        {t("No studysets created yet")}
                      </p>
                    </td>
                  </tr>
                ) : (
                  <>
                  {/* In-progress drafts (not yet created) — click to resume. */}
                  {drafts.map((draft) => (
                    <tr
                      key={draft.key}
                      style={{ fontSize: "14px", background: "#fffdf5" }}
                    >
                      <td
                        className="py-3 ps-4 exercise-title-link"
                        onClick={() => navigate(draft.resume)}
                        style={{
                          fontWeight: "bold",
                          color: "#b45309",
                          cursor: "pointer",
                        }}
                      >
                        {draft.title}
                        <span
                          className="ms-2 text-muted"
                          style={{ fontWeight: 400, fontSize: "12px" }}
                        >
                          ({draft.typeLabel})
                        </span>
                      </td>
                      <td className="py-3" style={{ color: "#858585" }}>
                        {subjectLabel(draft.sourceId) ? (
                          <>
                            <Flag
                              code={getFlagCode(subjectLabel(draft.sourceId))}
                              style={{ width: 24, height: 24, marginRight: 8 }}
                            />
                            {subjectLabel(draft.sourceId)}
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="py-3" style={{ color: "#858585" }}>
                        {subjectLabel(draft.targetId) ? (
                          <>
                            <Flag
                              code={getFlagCode(subjectLabel(draft.targetId))}
                              style={{ width: 24, height: 24, marginRight: 8 }}
                            />
                            {subjectLabel(draft.targetId)}
                          </>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="py-3" style={{ color: "#858585" }}>
                        {t("Not created yet")}
                      </td>
                      <td className="py-3" style={{ color: "#858585" }}>
                        {draft.size ?? "—"}
                      </td>
                      <td className="py-3">
                        <span
                          className="badge rounded-pill"
                          style={{
                            background: "#fef3c7",
                            color: "#b45309",
                            fontWeight: 600,
                          }}
                        >
                          {t("Draft")}
                        </span>
                      </td>
                      <td className="py-3" style={{ color: "#858585" }}>
                        —
                      </td>
                      <td className="py-3 pe-4" style={{ color: "#858585" }}>
                        —
                      </td>
                    </tr>
                  ))}
                  {vocabulary?.map((vocab, index) => (
                    <tr key={index} style={{ fontSize: "14px" }}>
                      <td
                        className="py-3 ps-4 exercise-title-link"
                        onClick={() => navigate(`/vocabulary/${vocab._id}`)}
                        style={{
                          fontWeight: "bold",
                          color: "#0056c6",
                          cursor: "pointer",
                        }}
                      >
                        {vocab?.title}
                      </td>
                      <td className="py-3" style={{ color: "#858585" }}>
                        <Flag
                          code={getFlagCode(vocab?.sourceLanguage?.title)}
                          style={{ width: 24, height: 24, marginRight: 8 }}
                        />
                        {vocab?.sourceLanguage?.title}
                      </td>
                      <td className="py-3" style={{ color: "#858585" }}>
                        <Flag
                          code={getFlagCode(vocab?.targetLanguage?.title)}
                          style={{ width: 24, height: 24, marginRight: 8 }}
                        />
                        {vocab?.targetLanguage?.title}
                      </td>
                      <td className="py-3" style={{ color: "#858585" }}>
                        {vocab?.description || t("No description")}
                      </td>
                      <td className="py-3" style={{ color: "#858585" }}>
                        {vocab?.maxSize}
                      </td>
                      <td className="py-3">
                        <span
                          className="badge rounded-pill"
                          style={{
                            background: "#dcfce7",
                            color: "#16a34a",
                            fontWeight: 600,
                          }}
                        >
                          {t("Created")}
                        </span>
                      </td>
                      <td className="py-3" style={{ color: "#858585" }}>
                        {vocab?.source || "Individual"}
                      </td>
                      <td className="py-3 pe-4" style={{ color: "#858585" }}>
                        {vocab?.assignedBy || "Admin"}
                      </td>
                    </tr>
                  ))}
                  </>
                )}
              </tbody>
            </Table>
          </div>

          <div className="exercise-footer">
            <CustomPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
      <DeleteSchoolModal
        show={showDeleteModal}
        handleClose={handleCloseModal}
        handleConfirmDelete={handleStudySetDelete}
      />
    </div>
  );
};

export default Vocabulary;
